import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  INITIAL_MARKETS,
  INITIAL_USER_BETS,
  INITIAL_LEADERBOARD,
} from '../api/mock-data';
import type {
  PredictionMarket,
  UserBet,
  BetSlipItem,
  MarketCategory,
  MarketStatus,
  PriceTick,
  PredictorLeaderboardItem,
} from '../types';
import { useWallet } from '@/features/wallet/hooks/use-wallet';

const MARKETS_STORAGE_KEY = 'predictx_markets_v1';
const USER_BETS_STORAGE_KEY = 'predictx_user_bets_v1';
const BETS_UPDATED_EVENT = 'predictx_bets_updated';
const BET_SLIP_EVENT = 'predictx_bet_slip_event';

// ---------------------------------------------------------------------------
// Global Bet Slip State
// ---------------------------------------------------------------------------

let activeBetSlip: BetSlipItem | null = null;
let isBetSlipOpen = false;

export function useBetSlip() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const handleUpdate = () => setTick((t) => t + 1);
    window.addEventListener(BET_SLIP_EVENT, handleUpdate);
    return () => window.removeEventListener(BET_SLIP_EVENT, handleUpdate);
  }, []);

  const selectBet = useCallback((bet: Omit<BetSlipItem, 'stake'>, defaultStake = 100) => {
    activeBetSlip = {
      ...bet,
      stake: defaultStake,
    };
    isBetSlipOpen = true;
    window.dispatchEvent(new Event(BET_SLIP_EVENT));
  }, []);

  const updateStake = useCallback((stake: number) => {
    if (activeBetSlip) {
      activeBetSlip = { ...activeBetSlip, stake: Math.max(1, stake) };
      window.dispatchEvent(new Event(BET_SLIP_EVENT));
    }
  }, []);

  const closeSlip = useCallback(() => {
    isBetSlipOpen = false;
    window.dispatchEvent(new Event(BET_SLIP_EVENT));
  }, []);

  const clearSlip = useCallback(() => {
    activeBetSlip = null;
    isBetSlipOpen = false;
    window.dispatchEvent(new Event(BET_SLIP_EVENT));
  }, []);

  return {
    item: activeBetSlip,
    isOpen: isBetSlipOpen,
    selectBet,
    updateStake,
    closeSlip,
    clearSlip,
  };
}

// ---------------------------------------------------------------------------
// Prediction Markets Store & Queries
// ---------------------------------------------------------------------------

function getStoredMarkets(): PredictionMarket[] {
  try {
    const raw = localStorage.getItem(MARKETS_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // fallback
  }
  return INITIAL_MARKETS;
}

export function useMarkets(filters?: {
  category?: MarketCategory | 'all';
  status?: MarketStatus | 'all';
  search?: string;
}) {
  return useQuery({
    queryKey: ['prediction-markets', filters],
    queryFn: async () => {
      const all = getStoredMarkets();
      return all.filter((m) => {
        if (filters?.category && filters.category !== 'all' && m.category !== filters.category) {
          return false;
        }
        if (filters?.status && filters.status !== 'all' && m.status !== filters.status) {
          return false;
        }
        if (filters?.search) {
          const q = filters.search.toLowerCase();
          return (
            m.title.toLowerCase().includes(q) ||
            m.description.toLowerCase().includes(q)
          );
        }
        return true;
      });
    },
    staleTime: 30 * 1000,
  });
}

export function useMarket(id: string) {
  return useQuery({
    queryKey: ['prediction-market', id],
    queryFn: async () => {
      const all = getStoredMarkets();
      const found = all.find((m) => m.id === id);
      if (!found) throw new Error('Prediction market not found');
      return found;
    },
  });
}

// ---------------------------------------------------------------------------
// User Bets
// ---------------------------------------------------------------------------

function getStoredUserBets(): UserBet[] {
  try {
    const raw = localStorage.getItem(USER_BETS_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // fallback
  }
  return INITIAL_USER_BETS;
}

export function useUserBets() {
  const queryClient = useQueryClient();
  const [bets, setBets] = useState<UserBet[]>(getStoredUserBets);

  const sync = useCallback(() => {
    setBets(getStoredUserBets());
  }, []);

  useEffect(() => {
    window.addEventListener(BETS_UPDATED_EVENT, sync);
    return () => window.removeEventListener(BETS_UPDATED_EVENT, sync);
  }, [sync]);

  const placeBetMutation = useMutation({
    mutationFn: async (input: {
      marketId: string;
      marketTitle: string;
      category: MarketCategory;
      optionId: string;
      optionLabel: string;
      odds: number;
      stake: number;
    }) => {
      const newBet: UserBet = {
        id: `bet-${Date.now()}`,
        marketId: input.marketId,
        marketTitle: input.marketTitle,
        category: input.category,
        optionId: input.optionId,
        optionLabel: input.optionLabel,
        odds: input.odds,
        stake: input.stake,
        potentialPayout: Math.round(input.stake * input.odds),
        status: 'active',
        createdAt: new Date().toISOString(),
      };

      const current = getStoredUserBets();
      const updated = [newBet, ...current];
      localStorage.setItem(USER_BETS_STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event(BETS_UPDATED_EVENT));
      return newBet;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-bets'] });
    },
  });

  const activeBets = bets.filter((b) => b.status === 'active');
  const wonBets = bets.filter((b) => b.status === 'won');
  const lostBets = bets.filter((b) => b.status === 'lost');

  const totalWagered = bets.reduce((acc, b) => acc + b.stake, 0);
  const totalWon = wonBets.reduce((acc, b) => acc + b.potentialPayout, 0);
  const winRate =
    wonBets.length + lostBets.length > 0
      ? Math.round((wonBets.length / (wonBets.length + lostBets.length)) * 100)
      : 0;

  return {
    bets,
    activeBets,
    wonBets,
    lostBets,
    placeBet: placeBetMutation.mutateAsync,
    isPlacing: placeBetMutation.isPending,
    stats: {
      totalWagered,
      totalWon,
      netProfit: totalWon - totalWagered,
      winRate,
      activeCount: activeBets.length,
    },
  };
}

// ---------------------------------------------------------------------------
// Leaderboard Query
// ---------------------------------------------------------------------------

export function useLeaderboard() {
  return useQuery<PredictorLeaderboardItem[]>({
    queryKey: ['leaderboard'],
    queryFn: async () => INITIAL_LEADERBOARD,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Fast Live Price Game Arena (30-second real-time round simulator)
// ---------------------------------------------------------------------------

const ROUND_DURATION = 30; // 30 seconds per round

export function useLivePriceGame() {
  const { creditWin } = useWallet();
  const [roundNumber, setRoundNumber] = useState(8492);
  const [secondsRemaining, setSecondsRemaining] = useState(24);
  const [lockPrice, setLockPrice] = useState(98450.25);
  const [currentPrice, setCurrentPrice] = useState(98462.1);
  const [priceHistory, setPriceHistory] = useState<PriceTick[]>(() => {
    const list: PriceTick[] = [];
    const base = 98440;
    for (let i = 15; i >= 0; i--) {
      list.push({
        time: `${i}s ago`,
        price: Number((base + Math.sin(i) * 18 + (Math.random() * 8 - 4)).toFixed(2)),
      });
    }
    return list;
  });
  const [myPosition, setMyPosition] = useState<{
    direction: 'UP' | 'DOWN';
    stake: number;
    multiplier: number;
    entryPrice: number;
  } | null>(null);
  const [lastRoundResult, setLastRoundResult] = useState<{
    winner: 'UP' | 'DOWN';
    won: boolean;
    payout: number;
  } | null>(null);

  // Price tick simulation & countdown loop
  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          // Round settled! Determine outcome
          setCurrentPrice((curr) => {
            const finalPrice = Number(
              (curr + (Math.random() * 12 - 5.8)).toFixed(2),
            );
            const winner: 'UP' | 'DOWN' = finalPrice >= lockPrice ? 'UP' : 'DOWN';

            // Check if user won
            if (myPosition) {
              const won = myPosition.direction === winner;
              const payout = won ? Math.round(myPosition.stake * myPosition.multiplier) : 0;
              if (won) {
                creditWin(payout, `Live Arena ${winner} Prediction Won!`);
              }
              setLastRoundResult({ winner, won, payout });
            } else {
              setLastRoundResult({ winner, won: false, payout: 0 });
            }

            // Reset for next round
            setMyPosition(null);
            setRoundNumber((r) => r + 1);
            setLockPrice(finalPrice);
            return finalPrice;
          });
          return ROUND_DURATION;
        }

        // Live tick inside round
        setCurrentPrice((curr) => {
          const delta = (Math.random() - 0.49) * 6.5;
          const newPrice = Number((curr + delta).toFixed(2));
          setPriceHistory((hist) => [
            ...hist.slice(-20),
            { time: `${new Date().toLocaleTimeString().slice(3, 8)}`, price: newPrice },
          ]);
          return newPrice;
        });

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [creditWin, lockPrice, myPosition]);

  const placePosition = useCallback(
    (direction: 'UP' | 'DOWN', stake: number, multiplier = 1.95) => {
      setMyPosition({
        direction,
        stake,
        multiplier,
        entryPrice: currentPrice,
      });
      setLastRoundResult(null);
    },
    [currentPrice],
  );

  const priceDelta = Number((currentPrice - lockPrice).toFixed(2));
  const isUp = priceDelta >= 0;

  return {
    roundNumber,
    pair: 'BTC/USDT' as const,
    secondsRemaining,
    totalDuration: ROUND_DURATION,
    lockPrice,
    currentPrice,
    priceDelta,
    isUp,
    priceHistory,
    myPosition,
    lastRoundResult,
    placePosition,
  };
}
